import React, { useState, useEffect, useRef, useId } from 'react';
import { RiCalendar2Line, RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';

/**
 * DatePicker Component
 * Custom date picker with calendar UI and mobile responsiveness
 *
 * @param {Object} props
 * @param {Date|null} props.value - Selected date
 * @param {Function} props.onChange - Callback when date changes
 * @param {Date|null} props.minDate - Minimum selectable date
 * @param {Date|null} props.maxDate - Maximum selectable date
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Whether picker is disabled
 * @param {string} props.label - Optional label for the input
 */
const DatePicker = ({
  value = null,
  onChange,
  minDate = null,
  maxDate = null,
  placeholder = 'Select date',
  error = '',
  disabled = false,
  label = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempSelectedDate, setTempSelectedDate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const inputRef = useRef(null);
  const calendarRef = useRef(null);
  const errorId = useId();

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Initialize current month from value
  useEffect(() => {
    if (value && value instanceof Date && !isNaN(value)) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  // Format date for display
  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) {
      return '';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Check if date is disabled
  const isDateDisabled = (date) => {
    if (!date) return true;

    if (minDate && date < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) {
      return true;
    }

    if (maxDate && date > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) {
      return true;
    }

    return false;
  };

  // Check if date is today
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is selected
  const isSelected = (date) => {
    if (!date) return false;
    const selectedDate = tempSelectedDate || value;
    if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate)) return false;

    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Handle date selection
  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    setTempSelectedDate(date);
    // Call onChange immediately when date is selected
    onChange?.(date);
    // Auto-close calendar for better UX (single click instead of click + Apply)
    setIsOpen(false);
  };

  // Handle Apply
  const handleApply = () => {
    if (tempSelectedDate) {
      onChange?.(tempSelectedDate);
      setIsOpen(false);
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    setTempSelectedDate(null);
    setIsOpen(false);
  };

  // Handle input click
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setTempSelectedDate(value);
    }
  };

  // Handle keyboard events on input
  const handleInputKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(true);
      setTempSelectedDate(value);
    }
  };

  // Handle keyboard events in calendar
  const handleCalendarKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Weekday headers
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get days for current month
  const days = getDaysInMonth(currentMonth);

  // Month year display
  const monthYearDisplay = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <div
          data-testid="calendar-icon"
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        >
          <RiCalendar2Line className="w-4 h-4 text-gray-700" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={formatDate(value)}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm transition-all cursor-pointer text-gray-700 placeholder-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            error && 'error border-red-500 focus:ring-red-500',
            disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
            !error && !disabled && 'border-gray-300 hover:border-gray-400'
          )}
        />
      </div>

      {/* Calendar Dialog */}
      {isOpen && !disabled && (
        <div
          ref={calendarRef}
          role="dialog"
          aria-label="Choose date"
          aria-modal="true"
          onKeyDown={handleCalendarKeyDown}
          className="popover absolute bottom-full mb-1 left-0 w-full z-50 bg-white border border-gray-300 shadow-xl p-4 rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              aria-label="Previous month"
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              tabIndex={0}
            >
              <RiArrowLeftSLine className="w-5 h-5 text-gray-600" />
            </button>
            <div className="font-semibold text-gray-900">{monthYearDisplay}</div>
            <button
              type="button"
              onClick={handleNextMonth}
              aria-label="Next month"
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              tabIndex={0}
            >
              <RiArrowRightSLine className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="p-2"></div>;
              }

              const disabled = isDateDisabled(date);
              const selected = isSelected(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  type="button"
                  role="button"
                  aria-label={formatDate(date)}
                  aria-selected={selected}
                  aria-disabled={disabled}
                  disabled={disabled}
                  onClick={() => handleDateClick(date)}
                  className={cn(
                    'p-2 text-sm rounded-lg transition-colors',
                    'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500',
                    today && 'today font-bold text-primary-600',
                    selected && 'selected bg-primary-600 text-white hover:bg-primary-700',
                    disabled && 'disabled text-gray-300 cursor-not-allowed hover:bg-transparent',
                    !selected && !disabled && 'text-gray-900'
                  )}
                  tabIndex={0}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer buttons */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              tabIndex={0}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!tempSelectedDate}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              tabIndex={0}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          id={errorId}
          className="mt-1 text-sm text-red-600"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
